/**
 * Année d'exercice / activation de la caisse.
 *
 * - Année de démarrage (ex. 2026) → liste = [2026]
 * - En 2027 → liste = [2026, 2027], ouverture par défaut sur 2027
 * - Clients / users restent globaux ; les pages métier filtrent par cette année
 * - Pas de nouvelle table SQL par année : 2026 reste visible via le select (archive)
 */

const STORAGE_KEY = 'cgm_caisse_active_year';
const EVENT_NAME = 'cgm-caisse-year-changed';
const LAST_SEEN_CALENDAR_KEY = 'cgm_caisse_last_calendar_year';

/** Première année d'activité de la caisse (surchargeable via REACT_APP_CAISSE_START_YEAR) */
export function getCaisseStartYear() {
  const fromEnv = parseInt(process.env.REACT_APP_CAISSE_START_YEAR || '', 10);
  if (Number.isFinite(fromEnv) && fromEnv >= 2000 && fromEnv <= 2100) {
    return fromEnv;
  }
  return 2026;
}

/**
 * Date à partir de laquelle on affiche le report caisse (J−1).
 * Avant cette date : pas de ligne « Montant caisse (jour−1) ».
 */
export function getCaisseCarryStartDate() {
  const fromEnv = String(process.env.REACT_APP_CAISSE_CARRY_START_DATE || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromEnv)) return fromEnv;
  return '2026-09-01';
}

/** Premier jour où l'état jour = Reste CGM année (pas les anciens reports). */
export function getCaisseResteAlignStartDate() {
  const fromEnv = String(process.env.REACT_APP_CAISSE_RESTE_ALIGN_START_DATE || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromEnv)) return fromEnv;
  return '2026-01-01';
}

/**
 * Ligne « Montant caisse (J−1) » :
 * - pas sur l'état année (Reste = honoraires − dépenses de l'année)
 * - pas le 1er janvier (entrée = 0)
 * - sinon si la période commence au 01/01/2026 ou après
 */
export function shouldShowCaisseCarryLine(previousClosingYmd, periodStartYmd, filterType) {
  if (filterType === 'annee') return false;
  const period = String(periodStartYmd || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(period)) {
    if (period.slice(5) === '01-01') return false;
    return period >= getCaisseResteAlignStartDate();
  }
  const d = String(previousClosingYmd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  return d >= getCaisseCarryStartDate();
}

export function getCalendarYear() {
  return new Date().getFullYear();
}

/**
 * Liste des années actives : de l'année de démarrage à l'année calendaire courante.
 * Tri décroissant (année courante en premier).
 */
export function buildCaisseYearOptions() {
  const start = getCaisseStartYear();
  const current = getCalendarYear();
  const years = [];
  for (let y = Math.max(start, current); y >= start; y -= 1) {
    years.push(y);
  }
  // Si on est avant le démarrage (horloge / tests), au moins l'année de start
  if (years.length === 0) years.push(start);
  return years;
}

function clampToActiveYears(year) {
  const options = buildCaisseYearOptions();
  const y = parseInt(year, 10);
  if (Number.isFinite(y) && options.includes(y)) return y;
  return options[0]; // année calendaire (ou start)
}

/**
 * Année de travail courante (localStorage, sinon année calendaire bornée).
 */
export function getActiveCaisseYear() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw != null && raw !== '') {
      return clampToActiveYears(raw);
    }
  } catch (_) {
    /* ignore */
  }
  return clampToActiveYears(getCalendarYear());
}

/**
 * Définit l'année de travail et notifie les autres écrans.
 */
export function setActiveCaisseYear(year) {
  const y = clampToActiveYears(year);
  try {
    localStorage.setItem(STORAGE_KEY, String(y));
  } catch (_) {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { year: y } }));
  } catch (_) {
    /* ignore */
  }
  return y;
}

/**
 * Au passage d'année calendaire (ex. 31/12/2026 23:59 → 01/01/2027 00:00) :
 * - ajoute la nouvelle année dans le select
 * - bascule l'exercice actif sur l'année calendaire (données 2027 = vides / à 0)
 * - les années passées restent consultables (archive via le select)
 *
 * Ne crée / ne supprime aucune table SQL.
 */
export function syncActiveCaisseYearWithCalendar() {
  const calendar = getCalendarYear();
  let lastSeen = null;
  try {
    const raw = localStorage.getItem(LAST_SEEN_CALENDAR_KEY);
    if (raw != null && raw !== '') lastSeen = parseInt(raw, 10);
  } catch (_) {
    /* ignore */
  }

  try {
    localStorage.setItem(LAST_SEEN_CALENDAR_KEY, String(calendar));
  } catch (_) {
    /* ignore */
  }

  // Nouveau calendrier détecté (PC / horloge) → ouvrir sur l'année en temps réel
  if (Number.isFinite(lastSeen) && calendar > lastSeen) {
    return setActiveCaisseYear(calendar);
  }

  // Première ouverture / pas de préférences → année calendaire
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null || raw === '') {
      return setActiveCaisseYear(calendar);
    }
  } catch (_) {
    return setActiveCaisseYear(calendar);
  }

  return getActiveCaisseYear();
}

/** Abonnement aux changements d'année (retourne une fonction unsubscribe). */
export function subscribeCaisseYear(listener) {
  const handler = (e) => {
    const y = e?.detail?.year != null ? e.detail.year : getActiveCaisseYear();
    listener(y);
  };
  const onStorage = (e) => {
    if (e.key === STORAGE_KEY || e.key === LAST_SEEN_CALENDAR_KEY) {
      listener(getActiveCaisseYear());
    }
  };
  const onFocus = () => {
    const y = syncActiveCaisseYearWithCalendar();
    listener(y);
  };
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', onFocus);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', onFocus);
  };
}

export const CAISSE_YEAR_EVENT = EVENT_NAME;
